/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

/**
 * Document Migration Modal Constants
 *
 * Ported from lego/page/browser/document-migration/FormValidation.jsx
 * and related legacy files
 */

/**
 * File size limits
 */
export const MAX_TOTAL_SIZE = 2560 * 1024 * 1024 // 2560 MB in bytes
export const MAX_FILE_SIZE = 2560 * 1024 * 1024 // Individual file size limit
export const DEFAULT_FILE_SIZE_LIMIT = 100 * 1024 * 1024 // 100 MB for checksum calculation

/**
 * Filename length limits
 */
export const MAXIMUM_FILENAME_LENGTH = 234
export const MAXIMUM_FILENAME_LENGTH_WINDOWS = 180

/**
 * Upload limits
 */
export const MAX_FILES_PER_UPLOAD = 50
export const MIN_FILES_PER_UPLOAD = 1

/**
 * Screen size breakpoints
 */
export const MOBILE_SCREEN_SIZE = 768

/**
 * Session prolonging interval (milliseconds)
 */
export const SESSION_PROLONG_INTERVAL = 60000 // 60 seconds

/**
 * System folder types
 */
export enum SystemFolderType {
  SITE = 'SITE',
  STUDY = 'STUDY',
  EXTERNAL = 'EXTERNAL',
  SYSTEM = 'SYSTEM'
}

/**
 * Folder names
 */
export enum FolderName {
  SITE_FILE = 'Site Files',
  EXTERNAL_TRANSFER = 'External Transfer',
  STUDY_ROOT = 'Study Root',
  TMF_ROOT = 'TMF Root'
}

/**
 * Document types allowed for specific folder types
 * Maps folder type to allowed document type names
 */
export const FOLDER_TYPE_DOCUMENT_TYPES_ALLOWED: Record<string, string[]> = {
  [SystemFolderType.SITE]: ['Site Document', 'Regulatory Document', 'Site File', 'Essential Document'],
  [SystemFolderType.EXTERNAL]: ['Transfer Document', 'External Document', 'Import Document'],
  [SystemFolderType.STUDY]: ['Study Document', 'Protocol', 'Amendment', 'ICF']
}

/**
 * Workflow processing types
 */
export enum WorkflowProcessingType {
  SERIAL = 'SERIAL',
  PARALLEL = 'PARALLEL',
  N_OF_ALL = 'N_OF_ALL'
}

/**
 * Workflow step types (for approvers, reviewers, collaborators)
 */
export enum WF_STEP_TYPE {
  NOT_DEFINED = 'NOT_DEFINED',
  USE_INITIATOR = 'USE_INITIATOR',
  SETUP_WITH_WORKFLOW = 'SETUP_WITH_WORKFLOW',
  USE_PREDEFINED = 'USE_PREDEFINED'
}

/**
 * Document workflow setup types
 */
export enum DocumentWorkflowType {
  SETUP_WITH_WORKFLOW = 'SETUP_WITH_WORKFLOW',
  SETUP_WITHOUT_WORKFLOW = 'SETUP_WITHOUT_WORKFLOW'
}

/**
 * Allowed image extensions for combining
 */
export const COMBINABLE_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff', '.tif']

/**
 * Allowed document extensions
 */
export const ALLOWED_DOCUMENT_EXTENSIONS = [
  // Documents
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  '.txt',
  '.rtf',
  '.odt',
  '.ods',
  '.odp',

  // Images
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.bmp',
  '.tiff',
  '.tif',
  '.svg',

  // Archives
  '.zip',
  '.rar',
  '.7z',
  '.tar',
  '.gz',

  // Other
  '.xml',
  '.csv',
  '.json',
  '.msg',
  '.eml'
]

/**
 * Default form values
 */
export const DEFAULT_FORM_VALUES = {
  files: [],
  typeData: {
    documentTypeObj: null,
    documentTypeTemplateId: null,
    folderTypeTemplateId: null,
    approvers: {
      steps: []
    },
    reviewers: {
      steps: []
    },
    collaborators: {
      steps: []
    }
  },
  createCR: false,
  expirationDate: null,
  continueToEditPhase: false,
  combineImages: false,
  description: ''
} as const

/**
 * Translation keys
 */
export const TRANSLATION_KEYS = {
  // Modal titles
  MODAL_TITLE_UPLOAD: 'UPLOAD_DOCUMENTS',
  MODAL_TITLE_MIGRATE: 'MIGRATE_DOCUMENTS',

  // Labels
  LABEL_SELECT_FILES: 'SELECT_FILES',
  LABEL_DOCUMENT_TYPE: 'DOCUMENT_TYPE',
  LABEL_FOLDER_TYPE: 'FOLDER_TYPE',
  LABEL_CREATE_CR: 'CREATE_CHANGE_REQUEST',
  LABEL_DESCRIPTION: 'DESCRIPTION',
  LABEL_EXPIRATION_DATE: 'EXPIRATION_DATE',
  LABEL_CONTINUE_EDIT: 'CONTINUE_TO_EDIT_PHASE',
  LABEL_COMBINE_IMAGES: 'COMBINE_IMAGES',
  LABEL_APPROVERS: 'APPROVERS',
  LABEL_REVIEWERS: 'REVIEWERS',
  LABEL_COLLABORATORS: 'COLLABORATORS',

  // Buttons
  BTN_UPLOAD: 'UPLOAD',
  BTN_START_MIGRATION: 'START_MIGRATION',
  BTN_CANCEL: 'CANCEL',
  BTN_CONFIRM: 'CONFIRM',
  BTN_ADD_FILES: 'ADD_FILES',
  BTN_CLEAR_ALL: 'CLEAR_ALL',

  // Messages
  MSG_UPLOAD_SUCCESS: 'DOCUMENTS_UPLOADED_SUCCESSFULLY',
  MSG_MIGRATION_SUCCESS: 'DOCUMENTS_MIGRATED_SUCCESSFULLY',
  MSG_UPLOAD_IN_PROGRESS: 'UPLOAD_IN_PROGRESS',
  MSG_CALCULATING_CHECKSUM: 'CALCULATING_CHECKSUM',
  MSG_DUPLICATE_FILE: 'DUPLICATE_FILE_DETECTED',
  MSG_FILE_TOO_LARGE: 'FILE_TOO_LARGE',
  MSG_TOO_MANY_FILES: 'TOO_MANY_FILES',

  // Errors
  ERR_UPLOAD_FAILED: 'UPLOAD_FAILED',
  ERR_INVALID_FILE_TYPE: 'INVALID_FILE_TYPE',
  ERR_NO_FILES_SELECTED: 'NO_FILES_SELECTED',
  ERR_DOCUMENT_TYPE_REQUIRED: 'DOCUMENT_TYPE_REQUIRED',
  ERR_DESCRIPTION_REQUIRED: 'DESCRIPTION_REQUIRED_FOR_CR',

  // Confirmations
  CONFIRM_BLINDING_STATUS: 'CONFIRM_BLINDING_STATUS_CHANGE',
  CONFIRM_CERTIFIED_COPY: 'CONFIRM_CERTIFIED_COPY',
  CONFIRM_BLINDING_MESSAGE: 'BLINDING_STATUS_WILL_CHANGE',
  CONFIRM_CERTIFIED_MESSAGE: 'FILE_WILL_BE_MARKED_CERTIFIED'
} as const

/**
 * Validation error messages
 */
export const VALIDATION_MESSAGES = {
  FILES_REQUIRED: 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR',
  FILES_MAX_COUNT: 'DOC_MIGRATION_ERR_MAX_DOCUMENTS',
  FILES_TOTAL_SIZE: 'DOC_MIGRATION_ERR_MAX_SIZE',
  FILE_SIZE: 'DOC_MIGRATION_ERR_MAX_SIZE_FILE',
  FILENAME_LENGTH: 'DOC_MIGRATION_ERR_MAX_FILENAME_LENGTH',
  FILENAME_LENGTH_WINDOWS: 'DOC_MIGRATION_ERR_MAX_FILENAME_LENGTH',
  DUPLICATE_FILES: 'DOC_MIGRATION_ERR_DUPLICATE_FILE_NAMES',
  INVALID_FILE_TYPE: 'DOC_MIGRATION_ERR_NOT_SUPPORTED_TYPE',

  DOCUMENT_TYPE_REQUIRED: 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR',
  FOLDER_TYPE_REQUIRED: 'DOC_MIGRATION_ERROR_NO_FOLDER_TYPE_SELECTED',
  EXPIRATION_DATE_REQUIRED: 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR',
  DESCRIPTION_REQUIRED: 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR',

  STEP_LABEL_INVALID: 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR',
  NOFALLVALUE_REQUIRED: 'SOME_AT_ONCE_ERROR_MSG',
  NOFALLVALUE_MIN: 'SOME_AT_ONCE_ERROR_MSG',
  USERS_GROUPS_REQUIRED: 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR',

  APPROVERS_REQUIRED: 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR',
  REVIEWERS_REQUIRED: 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
} as const

/**
 * API endpoints (relative to base URL)
 */
export const API_ENDPOINTS = {
  UPLOAD_FILES: '/api/files/upload',
  MIGRATE_DOCUMENTS: '/api/files/migration',
  GET_DOCUMENT_TYPES: '/api/documenttypes',
  GET_FOLDER_TYPES: '/api/foldertypes',
  GET_WORKFLOW_TEMPLATE: '/api/workflowtemplates',
  CALCULATE_CHECKSUM: '/api/files/checksum',
  PROLONG_SESSION: '/api/session/prolong'
} as const
